import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { User } from '../models/user.models';
import { environment } from '../../environments/environment';
import { RoleService } from './role.service';
import { Router } from '@angular/router';
import { Usuario } from 'app/models/usuario';
import { DataService } from './data.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  headers: HttpHeaders;
  usuario: Observable<Usuario>;
  tokenParam;
  currentUserFirebase;

  constructor(private http: HttpClient, private router: Router, private dataService: DataService) {}

  // Propiedades de AuthService
  set currentUser(usuario) {
    this.currentUserFirebase = usuario;
  }

  get currentUser() {
    return this.currentUserFirebase;
  }

  // Métodos originales de UserService
  getUsers(): Observable<User[]> {
    // Ya no usamos el fallback con datos mock
    return this.http.get<User[]>(`${environment.apiUrl}users`);
  }

  getUser(id: number): Observable<User> {
    return this.http.get<User>(`${environment.apiUrl}users/${id}`);
  }

  getUserByUid(uid: string): Observable<User | null> {
    return this.http.get<User>(`${environment.apiUrl}users/profile/${uid}`);
  }

  createUser(user: Omit<User, 'id'>): Observable<User> {
    return this.http.post<User>(`${environment.apiUrl}users`, user);
  }

  updateUser(id: number, user: Partial<User>): Observable<User> {
    return this.http.patch<User>(`${environment.apiUrl}users/${id}`, user);
  }

  deleteUser(id: number): Observable<boolean> {
    return this.http.delete<any>(`${environment.apiUrl}users/${id}`).pipe(map(() => true));
  }

  // Métodos de AuthService integrados
  singup(usuario: Usuario, pass: string): Observable<any> {
    return this.http.post(environment.apiUrl + 'signup', {
      email: usuario.email,
      nombre: usuario.nombre,
      rol: usuario.rol,
      password: pass,
      apellido: usuario.apellido,
    });
  }

  recuperarContrasena(email): Observable<any> {
    return this.http.post(environment.apiUrl + 'recuperarpass', { email });
  }

  cerrarSesion() {
    this.dataService.deleteVariablesSession();
    this.router.navigate(['/']);
  }

  getUsuarioById(id): Observable<Usuario | any> {
    return this.http.get(environment.apiUrl + 'users/' + id);
  }

  getUsuarioByEmail(email, token): Observable<Usuario | any> {
    return this.http.post(environment.apiUrl + 'users/email/', {
      email,
      token,
    });
  }

  cambiarPassword(usuario, password, token): Observable<Usuario | any> {
    return this.http.post(environment.apiUrl + 'cambiarpass/usuario/', {
      usuario,
      password,
      token,
    });
  }

  // obtenemos los permisos del usuario
  getPermisosUsuario(token: string): Observable<Usuario | any> {
    return this.http.get(environment.apiUrl + 'auth/usuario/permisos/menu');
  }

  getUsuarios(): Observable<Usuario | any> {
    return this.http.get(environment.apiUrl + 'users');
  }

  getUsuariosGeneralesByEmail(email): Observable<Usuario | any> {
    return this.http.get(environment.apiUrl + 'users/email/' + email);
  }

  searchUsuariosByTermino(term: string): Observable<Usuario | any> {
    return this.http.get(environment.apiUrl + 'users/find/' + term);
  }

  setHabilitarUsuario(idusuario: string, estado: boolean) {
    return this.http.patch(environment.apiUrl + 'users/' + idusuario, { validado: estado }, { headers: this.headers });
  }

  setRolUsuario(idusuario: string, permisos: any) {
    return this.http.patch(environment.apiUrl + 'users/' + idusuario, { permisos }, { headers: this.headers });
  }

  eliminarUsuario(id: number) {
    return this.http.delete(environment.apiUrl + 'users/' + id, {
      headers: this.headers,
    });
  }

  crearColaborador(usuario: Usuario): Observable<Usuario | any> {
    return this.http.post(environment.apiUrl + 'users', usuario);
  }

  crearColaboradorByNotificacion(user, idNotification): Observable<Usuario | any> {
    return this.http.post(environment.apiUrl + 'users/notification', {
      user,
      idNotification,
    });
  }

  login(userName, password) {
    return this.http.post(environment.apiUrl + 'login', {
      userName,
      password,
    });
  }

  getUID() {
    return sessionStorage.getItem('uid');
  }

  // Métodos específicos de UserService
  assignRoleToUser(userId: number, roleId: number): Observable<User> {
    return this.http.post<User>(`${environment.apiUrl}users/${userId}/roles/${roleId}`, {});
  }

  removeRoleFromUser(userId: number, roleId: number): Observable<User> {
    return this.http.delete<User>(`${environment.apiUrl}users/${userId}/roles/${roleId}`);
  }
}
